import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, CircleCheck, Info, Mic, Square, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useExerciseDatabase } from "@/lib/queries/useExerciseDatabase";
import { useOrgFeatureFlags } from "@/lib/queries/useOrgFeatureFlags";
import { streamWorkoutBuilder } from "@/lib/ai/streamWorkoutBuilder";
import {
  WorkoutTranscribeError,
  transcribeWorkoutAudio,
} from "@/lib/ai/transcribeWorkoutAudio";
import { formatRecordingTime } from "@/lib/ai/waveformBars";
import { useWorkoutVoice } from "@/components/workouts/ai/useWorkoutVoice";
import { applyWorkoutTool } from "@/lib/ai/applyWorkoutEdit";
import { firstThinkingLine } from "@/lib/ai/thinkingLine";
import { ensureWorkoutSections } from "@/lib/utils/workoutSections";
import type { ExerciseCollection, Workout } from "@/components/workouts/workoutsColumns";
import type { AiToolCall } from "@/lib/ai/types";
import { cn } from "@/lib/utils/ui";

export type AiWorkoutCommit = (producer: (workout: Workout) => Workout) => void;

interface AiWorkoutBarProps {
  exerciseCollection: ExerciseCollection[];
  commit: AiWorkoutCommit;
  onEdited?: () => void;
  /** Fixed to the viewport. Docked sits on the bottom of a relative parent. */
  placement?: "fixed" | "docked";
  className?: string;
}

const AiWorkoutBar = ({
  exerciseCollection,
  commit,
  onEdited,
  placement = "fixed",
  className,
}: AiWorkoutBarProps) => {
  const { aiWorkoutBuilder: enabled } = useOrgFeatureFlags();
  const { exercises: exerciseDatabase } = useExerciseDatabase();
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [thinking, setThinking] = useState("");
  const voice = useWorkoutVoice();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const thinkingRawRef = useRef("");
  const abortRef = useRef<AbortController | null>(null);
  const transcribeAbortRef = useRef<AbortController | null>(null);
  const collectionRef = useRef(exerciseCollection);
  collectionRef.current = exerciseCollection;
  const databaseRef = useRef(exerciseDatabase);
  databaseRef.current = exerciseDatabase;

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      transcribeAbortRef.current?.abort();
    };
  }, []);

  const resizeInput = () => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  const submit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      setInput("");
      thinkingRawRef.current = "";
      setThinking("");
      setIsStreaming(true);
      if (textareaRef.current) textareaRef.current.style.height = "auto";

      const removedOriginalPositions: number[] = [];
      const adjustExOrder = (aiOrder: number) =>
        aiOrder - removedOriginalPositions.filter((position) => position < aiOrder).length;

      let applied = 0;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const applyCall = (call: AiToolCall) => {
        if (call.name === "no_workout_change") return;
        let next = call;
        if (call.name === "remove_exercise") {
          const originalOrder = call.args.ex_order;
          next = {
            ...call,
            args: { ...call.args, ex_order: adjustExOrder(originalOrder) },
          };
          removedOriginalPositions.push(originalOrder);
        } else if (call.name === "replace_exercise") {
          next = {
            ...call,
            args: { ...call.args, ex_order: adjustExOrder(call.args.ex_order) },
          };
        } else if (call.name === "update_exercise") {
          next = {
            ...call,
            args: { ...call.args, ex_order: adjustExOrder(call.args.ex_order) },
          };
        }

        let changed = false;
        commit((current) => {
          const ensured = ensureWorkoutSections(current);
          const updated = applyWorkoutTool(ensured, next, (id) => {
            const key = id.trim().toLowerCase();
            const list = databaseRef.current ?? [];
            return (
              list.find((exercise) => exercise.id.toLowerCase() === key) ??
              list.find((exercise) => exercise.name.trim().toLowerCase() === key)
            );
          });
          changed = updated.exerciseCollection !== ensured.exerciseCollection;
          return changed ? updated : current;
        });
        if (!changed) return;
        applied += 1;
        onEdited?.();
      };

      try {
        await streamWorkoutBuilder({
          prompt: trimmed,
          currentWorkout: collectionRef.current.flatMap((group, exOrder) =>
            group.map((exercise) => ({
              ex_order: exOrder,
              name: exercise.name,
              tracking_type: exercise.tracking_type ?? "strength",
              sets: (exercise.sets ?? []).map((set) => ({
                startRange: set.startRange,
                endRange: set.endRange,
                targetTimeSeconds: set.targetTimeSeconds,
                targetDistanceMeters: set.targetDistanceMeters,
              })),
            })),
          ),
          signal: controller.signal,
          onEvent: (event) => {
            if (controller.signal.aborted) return;
            if (event.type === "text_delta") {
              thinkingRawRef.current += event.content;
              setThinking(firstThinkingLine(thinkingRawRef.current));
            } else if (event.type === "tool_call_complete") {
              applyCall(event.call);
            } else if (event.type === "done") {
              if (applied === 0) {
                toast("No workout instructions supplied", {
                  icon: <Info className="size-4" />,
                });
              } else {
                toast("Workout updated by AI", {
                  icon: <CircleCheck className="size-4" />,
                });
              }
            } else if (event.type === "error") {
              toast.error(event.message);
            }
          },
        });
      } finally {
        if (abortRef.current === controller) {
          setIsStreaming(false);
          setThinking("");
        }
      }
    },
    [commit, isStreaming, onEdited],
  );

  const beginRecording = async () => {
    if (isStreaming || isTranscribing || voice.phase === "recording") return;
    try {
      await voice.start();
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      const message = error instanceof Error ? error.message : "";
      if (
        name === "AbortError" ||
        /destroyed while requesting the microphone/i.test(message)
      ) {
        return;
      }
      if (
        name === "NotAllowedError" ||
        name === "NotFoundError" ||
        /microphone|permission|not allowed|not found/i.test(message)
      ) {
        toast.error("Microphone access is needed to record");
        return;
      }
      toast.error(
        error instanceof Error ? error.message : "Couldn't start recording",
      );
    }
  };

  const sendRecording = async () => {
    if (isTranscribing || isStreaming) return;
    setIsTranscribing(true);
    transcribeAbortRef.current?.abort();
    const controller = new AbortController();
    transcribeAbortRef.current = controller;
    try {
      const blob = await voice.finish();
      if (!blob) return;
      const text = await transcribeWorkoutAudio(blob, controller.signal);
      if (!text) {
        toast("Couldn't hear a workout instruction");
        return;
      }
      setIsTranscribing(false);
      await submit(text);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error(
        error instanceof WorkoutTranscribeError
          ? error.message
          : "Couldn't transcribe that recording",
      );
    } finally {
      setIsTranscribing(false);
    }
  };

  if (!enabled) return null;

  const isRecording = voice.phase === "recording";
  const isWorking = isStreaming || isTranscribing;

  return (
    <>
      {placement === "fixed" ? <div className="h-24" aria-hidden /> : null}
      <div
        className={cn(
          "z-30 flex justify-center px-4",
          placement === "fixed"
            ? "pointer-events-none fixed inset-x-0 bottom-6"
            : "pointer-events-none absolute inset-x-0 bottom-4",
          className,
        )}
      >
        <form
          className={cn(
            "pointer-events-auto relative w-full max-w-xl overflow-hidden rounded-2xl p-px shadow-[0_16px_40px_rgba(15,23,42,0.14)]",
            isWorking ? "bg-transparent" : "bg-primary/30",
          )}
          onSubmit={(event) => {
            event.preventDefault();
            if (isRecording) {
              void sendRecording();
              return;
            }
            void submit(input);
          }}
        >
          {isWorking ? (
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[140%] animate-ai-border-spin"
              style={{
                background:
                  "conic-gradient(from 0deg, hsl(var(--primary) / 0) 0deg, hsl(var(--primary) / 0) 270deg, hsl(var(--primary)) 325deg, hsl(var(--primary) / 0) 360deg)",
              }}
            />
          ) : null}
          <div className="relative flex items-end gap-2 rounded-[15px] bg-background px-3 py-2">
            {isWorking ? (
              <p className="min-h-10 min-w-0 flex-1 truncate py-2 text-sm text-muted-foreground">
                <span className="animate-text-shimmer bg-[linear-gradient(90deg,hsl(var(--muted-foreground))_0%,hsl(var(--foreground))_45%,hsl(var(--muted-foreground))_100%)] bg-[length:200%_auto] bg-clip-text text-transparent">
                  {isTranscribing && !isStreaming ? "Transcribing…" : thinking || "Thinking…"}
                </span>
              </p>
            ) : isRecording ? (
              <div className="flex min-h-10 min-w-0 flex-1 items-center gap-3 py-2">
                <Mic className="size-4 shrink-0 text-primary" aria-hidden />
                <span className="w-11 shrink-0 text-sm tabular-nums text-muted-foreground">
                  {formatRecordingTime(voice.elapsed)}
                </span>
                <div
                  ref={voice.containerRef}
                  className="h-6 min-w-0 flex-1 overflow-hidden"
                  aria-hidden
                />
              </div>
            ) : (
              <>
                {!input.trim() ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="mb-1 size-8 shrink-0 rounded-full"
                    aria-label="Record workout instruction"
                    onClick={() => {
                      void beginRecording();
                    }}
                  >
                    <Mic className="size-4" />
                  </Button>
                ) : null}
                <textarea
                  ref={textareaRef}
                  value={input}
                  rows={1}
                  placeholder="Describe a workout change"
                  aria-label="Workout AI"
                  className="max-h-24 min-h-10 flex-1 resize-none bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                  onChange={(event) => {
                    setInput(event.target.value);
                    resizeInput();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void submit(input);
                    }
                  }}
                />
              </>
            )}
            {isStreaming ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="mb-1 size-8 shrink-0 rounded-full"
                aria-label="Stop"
                onClick={() => abortRef.current?.abort()}
              >
                <Square className="size-3 fill-current" />
              </Button>
            ) : isTranscribing ? null : isRecording ? (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="mb-1 size-8 shrink-0 rounded-full"
                  aria-label="Cancel recording"
                  onClick={voice.cancel}
                >
                  <X className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  className="mb-1 size-8 shrink-0 rounded-full"
                  aria-label="Send recording"
                  onClick={() => {
                    void sendRecording();
                  }}
                >
                  <ArrowUp className="size-4" />
                </Button>
              </>
            ) : (
              <Button
                type="submit"
                size="icon"
                className="mb-1 size-8 shrink-0 rounded-full"
                disabled={!input.trim() || isTranscribing}
                aria-label="Send"
              >
                <ArrowUp className="size-4" />
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AiWorkoutBar;
